import React, { useState, useEffect, useCallback, useRef } from 'react';
import { resumeAudio, startAmbient } from '../utils/audioEngine';

const BOOT_LINES = [
  { text: 'NEURAL INTERFACE', status: 'ONLINE', delay: 400, type: 'success' },
  { text: 'CLAUDE AI CORE', status: 'LOADED', delay: 700, type: 'success' },
  { text: 'ELEVENLABS VOICE', status: 'ARMED', delay: 1000, type: 'success' },
  { text: 'NEXIS365 LINK', status: 'ACTIVE', delay: 1300, type: 'success' },
  { text: 'WEBSOCKET RELAY', status: 'ONLINE', delay: 1600, type: 'success' },
  { text: 'FULL DIAGNOSTICS', status: 'CLEAR', delay: 2000, type: 'success' },
];

export default function BootSequence({ onComplete }) {
  const [hasStarted, setHasStarted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [visibleLines, setVisibleLines] = useState([]);
  const [phase, setPhase] = useState('idle'); // idle, igniting, booting, online
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  const bootAudioRef = useRef(null);
  const bootAnimDone = useRef(false);

  const handleStart = useCallback(() => {
    if (hasStarted) return;
    setHasStarted(true);
    resumeAudio();
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const startupSound = new Audio(`${backendUrl}/api/static/jarvis_startup.mp3`);
    startupSound.volume = 0.9;
    startupSound.play().catch(() => {});
    bootAudioRef.current = startupSound;
    
    startupSound.onended = () => {
      bootAudioRef.current = null;
    };
  }, [hasStarted]);

  // Crossfade: fade out startup while fading in welcome, then call onComplete
  const doCrossfade = useCallback(() => {
    const bu = process.env.REACT_APP_BACKEND_URL || '';
    const welcome = new Audio(`${bu}/api/static/jarvis_welcome.mp3`);
    welcome.volume = 0;
    
    welcome.play().then(() => {
      // Fade in welcome over 1.5s
      let wVol = 0;
      const fadeIn = setInterval(() => {
        wVol = Math.min(wVol + 0.03, 0.85);
        welcome.volume = wVol;
        if (wVol >= 0.85) clearInterval(fadeIn);
      }, 50);
      
      // Simultaneously fade out startup over 1.5s
      if (bootAudioRef.current) {
        let sVol = bootAudioRef.current.volume;
        const fadeOut = setInterval(() => {
          sVol = Math.max(sVol - 0.04, 0);
          if (bootAudioRef.current) bootAudioRef.current.volume = sVol;
          if (sVol <= 0) {
            clearInterval(fadeOut);
            if (bootAudioRef.current) { bootAudioRef.current.pause(); bootAudioRef.current = null; }
          }
        }, 50);
      }
    }).catch(() => {});
    
    welcome.onended = () => {
      // Welcome done — start ambient
      setTimeout(() => startAmbient(), 200);
    };
    
    // Transition to HUD
    setTimeout(() => onComplete(), 300);
  }, [onComplete]);

  const skip = useCallback(() => {
    if (bootAudioRef.current) {
      bootAudioRef.current.pause();
      bootAudioRef.current = null;
    }
    onComplete();
  }, [onComplete]);

  // Canvas animation — cinematic reactor ignition
  useEffect(() => {
    if (!hasStarted) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const w = canvas.width = window.innerWidth;
    const h = canvas.height = window.innerHeight;
    const cx = w / 2;
    const cy = h / 2;
    let frame = 0;

    // Particles
    const particles = [];
    for (let i = 0; i < 100; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 50 + Math.random() * 300;
      particles.push({
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        vx: (Math.random() - 0.5) * 1.5,
        vy: (Math.random() - 0.5) * 1.5,
        size: Math.random() * 2 + 0.5,
        alpha: Math.random() * 0.6 + 0.2,
        speed: 0.5 + Math.random() * 1.5,
      });
    }

    // Circuit lines
    const circuits = [];
    for (let i = 0; i < 20; i++) {
      const startAngle = Math.random() * Math.PI * 2;
      const r = 120 + Math.random() * 200;
      circuits.push({
        sx: cx + Math.cos(startAngle) * r,
        sy: cy + Math.sin(startAngle) * r,
        angle: startAngle,
        len: 30 + Math.random() * 80,
        alpha: 0,
        targetAlpha: Math.random() * 0.3 + 0.1,
        delay: Math.random() * 60,
      });
    }

    function draw() {
      frame++;
      ctx.clearRect(0, 0, w, h);

      const bootProgress = Math.min(frame / 180, 1); // ~3 seconds at 60fps
      const reactorIntensity = Math.min(frame / 120, 1);

      // Background radial gradient
      const bgGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * 0.6);
      bgGrad.addColorStop(0, `rgba(0, 20, 40, ${0.3 * reactorIntensity})`);
      bgGrad.addColorStop(0.5, 'rgba(0, 5, 15, 0.1)');
      bgGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, w, h);

      // Scanning rings expanding outward
      for (let r = 0; r < 5; r++) {
        const ringFrame = (frame * 0.8 + r * 40) % 200;
        const ringRadius = ringFrame * 2.5;
        const ringAlpha = Math.max(0, 0.15 - ringFrame * 0.00075) * reactorIntensity;
        if (ringAlpha > 0.01) {
          ctx.beginPath();
          ctx.arc(cx, cy, ringRadius, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(0, 215, 255, ${ringAlpha})`;
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }

      // Rotating outer ring segments
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(frame * 0.008);
      for (let i = 0; i < 12; i++) {
        const a1 = (i * 30 + 3) * Math.PI / 180;
        const a2 = ((i + 1) * 30 - 3) * Math.PI / 180;
        ctx.beginPath();
        ctx.arc(0, 0, 160 * reactorIntensity, a1, a2);
        ctx.strokeStyle = `rgba(0, 215, 255, ${0.3 * reactorIntensity})`;
        ctx.lineWidth = 2;
        ctx.stroke();
      }
      // Inner ring
      ctx.rotate(-frame * 0.012);
      for (let i = 0; i < 8; i++) {
        const a1 = (i * 45 + 5) * Math.PI / 180;
        const a2 = ((i + 1) * 45 - 5) * Math.PI / 180;
        ctx.beginPath();
        ctx.arc(0, 0, 110 * reactorIntensity, a1, a2);
        ctx.strokeStyle = `rgba(0, 215, 255, ${0.2 * reactorIntensity})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
      // Tick marks
      for (let i = 0; i < 36; i++) {
        const a = (i * 10) * Math.PI / 180;
        const r1 = 155 * reactorIntensity;
        const r2 = (i % 3 === 0 ? 170 : 163) * reactorIntensity;
        ctx.beginPath();
        ctx.moveTo(r1 * Math.cos(a), r1 * Math.sin(a));
        ctx.lineTo(r2 * Math.cos(a), r2 * Math.sin(a));
        ctx.strokeStyle = `rgba(0, 215, 255, ${(i % 3 === 0 ? 0.5 : 0.15) * reactorIntensity})`;
        ctx.lineWidth = i % 3 === 0 ? 2 : 0.8;
        ctx.stroke();
      }
      ctx.restore();

      // Core glow — grows with reactor intensity
      const coreSize = 40 * reactorIntensity;
      const pulse = 0.8 + 0.2 * Math.sin(frame * 0.1);
      const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreSize * 2.5);
      coreGrad.addColorStop(0, `rgba(0, 215, 255, ${0.7 * pulse * reactorIntensity})`);
      coreGrad.addColorStop(0.3, `rgba(0, 215, 255, ${0.3 * pulse * reactorIntensity})`);
      coreGrad.addColorStop(0.6, `rgba(0, 100, 200, ${0.1 * reactorIntensity})`);
      coreGrad.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(cx, cy, coreSize * 2.5, 0, Math.PI * 2);
      ctx.fillStyle = coreGrad;
      ctx.fill();

      // Core bright center
      ctx.beginPath();
      ctx.arc(cx, cy, coreSize * 0.4, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(180, 240, 255, ${0.8 * reactorIntensity})`;
      ctx.fill();

      // Circuit lines appearing
      circuits.forEach(c => {
        if (frame > c.delay) {
          c.alpha = Math.min(c.alpha + 0.02, c.targetAlpha * reactorIntensity);
          ctx.beginPath();
          ctx.moveTo(c.sx, c.sy);
          const ex = c.sx + Math.cos(c.angle) * c.len;
          const ey = c.sy + Math.sin(c.angle) * c.len;
          ctx.lineTo(ex, ey);
          // Right angle turn
          const turn = c.angle + Math.PI / 2;
          ctx.lineTo(ex + Math.cos(turn) * c.len * 0.5, ey + Math.sin(turn) * c.len * 0.5);
          ctx.strokeStyle = `rgba(0, 215, 255, ${c.alpha})`;
          ctx.lineWidth = 1;
          ctx.stroke();
          // Node dot
          ctx.beginPath();
          ctx.arc(c.sx, c.sy, 2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(0, 215, 255, ${c.alpha * 2})`;
          ctx.fill();
        }
      });

      // Particles pulled toward center during ignition
      particles.forEach(p => {
        const dx = cx - p.x;
        const dy = cy - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const pull = reactorIntensity * 0.3;
        p.x += p.vx + (dx / dist) * pull;
        p.y += p.vy + (dy / dist) * pull;
        if (dist < 30) {
          // Reset particle to edge
          const a = Math.random() * Math.PI * 2;
          const d = 200 + Math.random() * 200;
          p.x = cx + Math.cos(a) * d;
          p.y = cy + Math.sin(a) * d;
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 215, 255, ${p.alpha * reactorIntensity})`;
        ctx.fill();
      });

      // Connecting lines between nearby particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0, 215, 255, ${0.06 * (1 - dist / 100) * reactorIntensity})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      // Horizontal scan line
      const scanY = cy + Math.sin(frame * 0.03) * h * 0.4;
      const scanGrad = ctx.createLinearGradient(0, scanY - 2, 0, scanY + 2);
      scanGrad.addColorStop(0, 'transparent');
      scanGrad.addColorStop(0.5, `rgba(0, 215, 255, ${0.08 * reactorIntensity})`);
      scanGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = scanGrad;
      ctx.fillRect(0, scanY - 2, w, 4);

      animRef.current = requestAnimationFrame(draw);
    }

    draw();

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [hasStarted]);

  // Boot timing
  useEffect(() => {
    if (!hasStarted) return;
    setPhase('igniting');

    const progressTimer = setInterval(() => {
      setProgress(p => {
        if (p >= 100) { clearInterval(progressTimer); return 100; }
        return p + 1.2;
      });
    }, 35);

    setTimeout(() => setPhase('booting'), 1000);

    BOOT_LINES.forEach(line => {
      setTimeout(() => setVisibleLines(prev => [...prev, line]), line.delay + 600);
    });

    setTimeout(() => {
      setPhase('online');
      // Start crossfade: startup fades out, welcome fades in
      doCrossfade();
    }, 4200);

    return () => clearInterval(progressTimer);
  }, [hasStarted, onComplete]);

  return (
    <div className="boot-overlay" data-testid="boot-sequence" onClick={handleStart} style={{ cursor: hasStarted ? 'default' : 'pointer' }}>
      {/* Full-screen canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: hasStarted ? 1 : 0,
          transition: 'opacity 0.5s',
        }}
      />

      {/* Content overlay */}
      <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '20px' }}>
        {!hasStarted ? (
          <>
            <div style={{
              width: '140px', height: '140px', borderRadius: '50%',
              border: '2px solid rgba(0, 215, 255, 0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'radial-gradient(circle, rgba(0,215,255,0.12), transparent 70%)',
              animation: 'reactorPulse 2s ease-in-out infinite', cursor: 'pointer',
              boxShadow: '0 0 40px rgba(0,215,255,0.15), inset 0 0 20px rgba(0,215,255,0.1)',
            }}>
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
                <polygon points="6,3 20,12 6,21" fill="rgba(0,215,255,0.25)" stroke="rgba(0,215,255,0.7)" strokeWidth="1.5" />
              </svg>
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '15px', color: 'var(--cyan)', letterSpacing: '0.3em', textTransform: 'uppercase', textShadow: '0 0 20px rgba(0,215,255,0.5)' }}>
              INITIALIZE J.A.R.V.I.S
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'rgba(185,243,255,0.4)', letterSpacing: '0.15em' }}>
              CLICK TO ACTIVATE SYSTEMS
            </div>
          </>
        ) : (
          <>
            {/* Boot log — positioned lower */}
            <div style={{ position: 'absolute', bottom: '120px', left: '50%', transform: 'translateX(-50%)', width: '400px', maxWidth: '85vw' }}>
              {/* Progress bar */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--font-mono)', fontSize: '10px', marginBottom: '4px' }}>
                  <span style={{ color: 'rgba(0,215,255,0.6)', letterSpacing: '0.15em' }}>SYSTEM INITIALIZATION</span>
                  <span style={{ color: 'var(--cyan)' }}>{Math.round(progress)}%</span>
                </div>
                <div style={{ height: '2px', background: 'rgba(0,215,255,0.1)', borderRadius: '1px', overflow: 'hidden' }}>
                  <div data-testid="boot-sequence-progress" style={{ height: '100%', width: `${progress}%`, background: 'var(--cyan)', boxShadow: '0 0 12px var(--cyan)', transition: 'width 0.1s' }} />
                </div>
              </div>

              {/* Boot lines */}
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
                {visibleLines.map((line, i) => (
                  <div key={i} className="boot-log-line" style={{ animationDelay: `${i * 0.03}s`, display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span>
                      <span style={{ color: 'var(--success)' }}>[OK] </span>
                      <span style={{ color: 'rgba(185,243,255,0.8)' }}>{line.text}</span>
                    </span>
                    <span style={{ color: 'var(--success)', fontWeight: 500 }}>{line.status}</span>
                  </div>
                ))}
              </div>
            </div>

            <button className="boot-skip" onClick={(e) => { e.stopPropagation(); skip(); }} data-testid="boot-sequence-skip-button" style={{ position: 'absolute', bottom: '30px' }}>
              SKIP [ESC]
            </button>
          </>
        )}
      </div>
    </div>
  );
}
